import { Transform } from 'konva/lib/Util';
import { Rect, type RectConfig } from 'konva/lib/shapes/Rect';
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Rect as RectNode } from 'react-konva';
import type { LayerPatternMode } from '../../UIData/_stores/DesignLab/DesignLabLayerStore';
import { isBrowser, isNode } from "browser-or-node";
import { ImageLibraryContext } from '../_components/ImageLibrary';
import createCanvas from '../Utils/createCanvas';

export type Props = {
    src: string
    imageWidth: number
    imageHeight: number
    patternMode?:LayerPatternMode
    onLoad?: () => void
} & RectConfig;

const KonvaPatternImage = forwardRef<Rect | null, Props>(function KonvaPatternImage(attributes, ref) {
    const props = attributes as Props;
    const innerRef = useRef<Rect|null>(null);
    const patternCanvasRef = useRef<ReturnType<typeof createCanvas> | null>(null);
    const imageRef = useRef<CanvasImageSource|null>(null);
    const [image,setImage] = useState<ReturnType<typeof createCanvas>|null>(null);
    const [imageDensity,setImageDensity] = useState({ x: 1, y: 1 });
    const imageLibrary = useContext(ImageLibraryContext)
    const bounded = isNode && imageLibrary.bounded && (!imageLibrary.images.has(props.src) ||
        props.imageWidth * props.imageHeight > imageLibrary.bounded.atlasPixelLimit ||
        Math.max(props.imageWidth, props.imageHeight) * 2 > imageLibrary.bounded.atlasSideLimit)
        ? imageLibrary.bounded : undefined;

    const repeat = useMemo(() => {
        switch(props.patternMode) {
            case 'repeat-mirror':
            case 'repeat-mirror-x':
            case 'repeat-mirror-y':
            case 'half-drop':
            case 'half-brick':
                return 'repeat';

            case 'repeat-x-mirror-x':
                return 'repeat-x';

            case 'repeat-y-mirror-y':
                return 'repeat-y';

            default:
                return props.patternMode;
        }
    }, [props.patternMode])

    const handleLoad = useCallback(() => {
        if(imageRef.current === null) return
        if(props.imageWidth <= 0 || props.imageHeight <= 0) return;
        let imageWidth = props.imageWidth;
        let imageHeight = props.imageHeight;
        if(isBrowser) {
            const browserImage = imageRef.current as unknown as HTMLImageElement;

            if(!browserImage.complete || browserImage.naturalWidth === 0) return;
            // Rasterize at the preview's resolution, independently of the layer's logical tile size.
            // Mirrored tiles double either side. Keep those canvases within common browser limits.
            const scale = Math.min(1, 8192 / Math.max(browserImage.naturalWidth, browserImage.naturalHeight));
            imageWidth = Math.max(1, Math.floor(browserImage.naturalWidth * scale));
            imageHeight = Math.max(1, Math.floor(browserImage.naturalHeight * scale));
        }

        const density = { x: imageWidth / props.imageWidth, y: imageHeight / props.imageHeight };
        const halfWidth = Math.floor(props.imageWidth / 2) * density.x;
        const halfHeight = Math.floor(props.imageHeight / 2) * density.y;

        patternCanvasRef.current = createCanvas(imageWidth, imageHeight);
        if (isNode) imageLibrary.bounded?.trackCanvas(patternCanvasRef.current);
        const patternCtx = patternCanvasRef.current.getContext("2d") as CanvasRenderingContext2D | null;
        if(!patternCtx) return;

        switch(props.patternMode) {
            case 'repeat-mirror':
                patternCanvasRef.current.width = imageWidth*2;
                patternCanvasRef.current.height = imageHeight*2;
                patternCtx.drawImage(imageRef.current, 0, 0, imageWidth, imageHeight);

                patternCtx.save();
                patternCtx.scale(-1, 1);
                patternCtx.drawImage(imageRef.current, -imageWidth*2, 0, imageWidth, imageHeight);
                patternCtx.restore();

                patternCtx.scale(1, -1);
                patternCtx.drawImage(imageRef.current, 0, -imageHeight*2, imageWidth, imageHeight);
                patternCtx.scale(-1, 1);
                patternCtx.drawImage(imageRef.current, -imageWidth*2, -imageHeight*2, imageWidth, imageHeight);
                break;

            case 'repeat-mirror-x':
            case 'repeat-x-mirror-x':
                patternCanvasRef.current.width = imageWidth*2;
                patternCanvasRef.current.height = imageHeight;
                patternCtx.drawImage(imageRef.current, 0, 0, imageWidth, imageHeight);

                patternCtx.scale(-1, 1);
                patternCtx.drawImage(imageRef.current, -imageWidth*2, 0, imageWidth, imageHeight);
                break;

            case 'repeat-mirror-y':
            case 'repeat-y-mirror-y':
                patternCanvasRef.current.width = imageWidth;
                patternCanvasRef.current.height = imageHeight*2;
                patternCtx.drawImage(imageRef.current, 0, 0, imageWidth, imageHeight);

                patternCtx.scale(1, -1);
                patternCtx.drawImage(imageRef.current, 0, -imageHeight*2, imageWidth, imageHeight);
                break;

            case 'half-drop':
                patternCanvasRef.current.width = imageWidth*2;
                patternCanvasRef.current.height = imageHeight;
                patternCtx.drawImage(imageRef.current, 0, 0, imageWidth, imageHeight);
                patternCtx.drawImage(imageRef.current, imageWidth, -halfHeight, imageWidth, imageHeight);
                patternCtx.drawImage(imageRef.current, imageWidth, halfHeight, imageWidth, imageHeight);
                break;

            case 'half-brick':
                patternCanvasRef.current.width = imageWidth;
                patternCanvasRef.current.height = imageHeight*2;
                patternCtx.drawImage(imageRef.current, 0, 0, imageWidth, imageHeight);
                patternCtx.drawImage(imageRef.current, -halfWidth, imageHeight, imageWidth, imageHeight);
                patternCtx.drawImage(imageRef.current, halfWidth, imageHeight, imageWidth, imageHeight);
                break;

            default:
                patternCanvasRef.current.width = imageWidth;
                patternCanvasRef.current.height = imageHeight;
                patternCtx.drawImage(imageRef.current, 0, 0, imageWidth, imageHeight);
                break;
        }

        if(isBrowser) {
            setImageDensity(density);
            setImage(patternCanvasRef.current);
        }

        //Apply filters to pattern canvas
        if(props.filters !== undefined && props.filters.length > 0) {
            const imageData = patternCtx.getImageData(0, 0, patternCanvasRef.current.width, patternCanvasRef.current.height);
            for(const filter of props.filters) {
                (filter as (data: ImageData) => void).call(innerRef.current ?? new Rect(props), imageData);
                patternCtx.putImageData(imageData, 0, 0);
            }
        }
    }, [props.patternMode, props.imageWidth, props.imageHeight, props.filters, imageLibrary]);

    const loadHandlerRef = useRef(handleLoad);
    loadHandlerRef.current = handleLoad;

    useEffect(() => {
        if(isBrowser) {      
            const tmpImg = new window.Image();
            const onLoad = () => loadHandlerRef.current();

            imageRef.current = tmpImg;

            tmpImg.crossOrigin = 'anonymous';
            tmpImg.addEventListener('load', onLoad);
            tmpImg.src = props.src;

            return () => {
                tmpImg.removeEventListener('load', onLoad);
                imageRef.current = null;
            }
        }
    }, [props.src]);

    useEffect(() => {
       if(!isBrowser) return;
       handleLoad(); 
    }, [handleLoad]);

    if(isNode && bounded === undefined) {
        imageRef.current = imageLibrary.retrieveImage(props.src) as unknown as CanvasImageSource;
        handleLoad();
    }

    useEffect(() => {
        if (bounded === undefined) props.onLoad?.();
    }, [image, bounded]);

    useImperativeHandle<Rect | null, Rect | null>(ref, () => innerRef.current, [])

    const {src, width, height, fillPatternX, fillPatternY, fillPatternScaleX, fillPatternScaleY,
        fillPatternOffsetX, fillPatternOffsetY, offsetX, offsetY, filters, ...otherProps} = props;

    const newWidth = useMemo(() => {
        if(repeat !== undefined && ['no-repeat','repeat-y'].includes(repeat)) {
            return props.imageWidth*(props.fillPatternScaleX ?? 1)
        }

        return width ?? 0;
    }, [repeat, width, props.imageWidth, props.fillPatternScaleX]);

    const [newPatternX, newOffsetX] = useMemo(() => {
        if(repeat !== undefined && ['no-repeat','repeat-y'].includes(repeat)) {
            return [newWidth/2, newWidth/2];
        }

        return [fillPatternX, offsetX];
    }, [repeat, newWidth, fillPatternX, offsetX]);

    const newHeight = useMemo(() => {
        if(repeat !== undefined && ['no-repeat','repeat-x'].includes(repeat)) {
            return props.imageHeight*(props.fillPatternScaleY ?? 1)
        }

        return height ?? 0;
    }, [repeat, height, props.imageHeight, props.fillPatternScaleY]);

    const [newPatternY, newOffsetY] = useMemo(() => {
        if(repeat !== undefined && ['no-repeat','repeat-x'].includes(repeat)) {
            return [newHeight/2, newHeight/2];
        }

        return [fillPatternY, offsetY];
    }, [repeat, newHeight, fillPatternY, offsetY]);
    
    return <RectNode ref={innerRef}
        // @ts-expect-error -- Konva accepts native canvases; its pattern prop only declares images.
        fillPatternImage={isNode ? patternCanvasRef.current : image}
        fillPatternRepeat={repeat !== 'none' ? 'repeat' : 'none'}
        width={newWidth}
        height={newHeight}
        fillPatternX={newPatternX}
        fillPatternY={newPatternY}
        fillPatternScaleX={(fillPatternScaleX ?? 1) / imageDensity.x}
        fillPatternScaleY={(fillPatternScaleY ?? 1) / imageDensity.y}
        fillPatternOffsetX={(fillPatternOffsetX ?? 0) * imageDensity.x}
        fillPatternOffsetY={(fillPatternOffsetY ?? 0) * imageDensity.y}
        offsetX={newOffsetX}
        offsetY={newOffsetY}
        {...otherProps}
        sceneFunc={bounded === undefined ? props.sceneFunc : (context, shape) => {
            const ctx = context._context;
            const shapeMatrix = ctx.getTransform();
            const transform = new Transform();
            transform.translate(shape.fillPatternX(), shape.fillPatternY());
            transform.rotate(shape.fillPatternRotation() * Math.PI / 180);
            transform.scale(shape.fillPatternScaleX(), shape.fillPatternScaleY());
            transform.translate(-shape.fillPatternOffsetX(), -shape.fillPatternOffsetY());
            const matrix = transform.getMatrix();
            const pixelFilters = props.filters?.map(filter => {
                if (typeof filter !== 'function') throw new Error('Unsupported native pixel filter');
                return filter;
            });
            ctx.save();
            try {
                ctx.transform(matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5]);
                bounded.drawPattern(ctx, src, props.imageWidth, props.imageHeight, props.patternMode ?? 'repeat', pixelFilters,
                    { shapeMatrix, patternMatrix: matrix, width: shape.width(), height: shape.height(), shape });
            } finally {
                ctx.restore();
            }
        }}
    />
});

export default KonvaPatternImage;
